import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import dayjs from "dayjs";
import { DashboardAntDatePicker } from "src/components/reusable/antdDatePicker/DashboardAntDatePicker";
import { arabicCalendarLocale } from "src/components/reusable/antdDatePicker/arabicCalendarLocale";
import { getNumberLocale } from "src/utils/numberFormat";
import "./OrganicDateRangePicker.scss";

interface OrganicDateRangePickerProps {
  startDate: string;
  endDate: string;
  maxDate?: string;
  maxPastMonths?: number;
  allowUnlimitedHistory?: boolean;
  // eslint-disable-next-line no-unused-vars
  onRangeChange(range: { startDate: string; endDate: string }): void;
}

const OrganicDateRangePicker: FC<OrganicDateRangePickerProps> = ({
  startDate,
  endDate,
  maxDate,
  onRangeChange,
}) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("ar");
  const antdLocale = isAr ? arabicCalendarLocale : enUS;
  const numberDisplayLocale = useMemo(() => getNumberLocale(i18n.language), [i18n.language]);
  const [localStart, setLocalStart] = useState<dayjs.Dayjs | null>(() =>
    startDate ? dayjs(startDate, "YYYY-MM-DD") : null,
  );
  const [localEnd, setLocalEnd] = useState<dayjs.Dayjs | null>(() =>
    endDate ? dayjs(endDate, "YYYY-MM-DD") : null,
  );

  useEffect(() => {
    setLocalStart(startDate ? dayjs(startDate, "YYYY-MM-DD") : null);
    setLocalEnd(endDate ? dayjs(endDate, "YYYY-MM-DD") : null);
  }, [startDate, endDate]);

  const maxDay = useMemo(
    () =>
      maxDate ? dayjs(maxDate, "YYYY-MM-DD") : dayjs().endOf("day"),
    [maxDate]
  );

  const formatDisplayDate = useCallback(
    (date: dayjs.Dayjs) => {
      if (isAr) {
        return new Intl.DateTimeFormat(numberDisplayLocale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(date.toDate());
      }
      return date.format("DD-MM-YYYY");
    },
    [isAr, numberDisplayLocale]
  );

  const handleStartChange = (date: dayjs.Dayjs | null) => {
    setLocalStart(date);

    if (date && localEnd && !localEnd.isBefore(date, "day")) {
      onRangeChange({
        startDate: date.format("YYYY-MM-DD"),
        endDate: localEnd.format("YYYY-MM-DD"),
      });
    }
  };

  const handleEndChange = (date: dayjs.Dayjs | null) => {
    setLocalEnd(date);

    if (!localStart || !date) {
      return;
    }

    onRangeChange({
      startDate: localStart.format("YYYY-MM-DD"),
      endDate: date.format("YYYY-MM-DD"),
    });
  };

  return (
    <ConfigProvider locale={antdLocale} direction={isAr ? "rtl" : "ltr"}>
      <div className="organic-range-picker">
        <DashboardAntDatePicker
          value={localStart}
          format={isAr ? formatDisplayDate : "DD-MM-YYYY"}
          onChange={handleStartChange}
          className="dashboard-date-picker"
          placement="bottomLeft"
          allowClear={false}
          placeholder={t("organicMedia.datePicker.startDate")}
          disabledDate={current => current.isAfter(maxDay.endOf("day"))}
        />
        <DashboardAntDatePicker
          value={localEnd}
          format={isAr ? formatDisplayDate : "DD-MM-YYYY"}
          onChange={handleEndChange}
          className="dashboard-date-picker"
          placement="bottomLeft"
          allowClear={false}
          placeholder={t("organicMedia.datePicker.endDate")}
          disabledDate={current => {
            if (current.isAfter(maxDay.endOf("day"))) {
              return true;
            }

            return localStart ? current.isBefore(localStart, "day") : false;
          }}
        />
      </div>
    </ConfigProvider>
  );
};

export default OrganicDateRangePicker;
